// Statistics Service - Dashboard statistics API calls

import api from './api';

export interface OverviewStats {
  win_rate: number;
  win_rate_change: number | null;
  response_time_avg: number | null;
  response_time_change: number | null;
  routing_accuracy: number | null;
  routing_accuracy_change: number | null;
  revenue_ytd: number;
  revenue_ytd_change: number | null;
  active_quotes: number;
}

export interface TrendDataPoint {
  period: string;
  value: number;
  previous_value: number | null;
}

export interface SalespersonStats {
  name: string;
  salesperson_no: string;
  quotes_count: number;
  win_rate: number;
  win_rate_change: number | null;
  revenue: number;
}

export interface QuotePipelineStats {
  won_value: number;
  won_count: number;
  pending_value: number;
  pending_count: number;
  expired_value: number;
  expired_count: number;
  lost_value: number;
  lost_count: number;
}

export interface RoutingSummary {
  total_processed: number;
  match_rate: number;
  match_rate_change: number | null;
  avg_processing_time: number | null;
  accuracy: number | null;
  accuracy_change: number | null;
  by_status: Record<string, number>;
}

export interface SalespersonOption {
  salesperson_no: string;
  name: string;
}

export interface SegmentOption {
  value: string;
  count: number;
}

export interface FilterOptions {
  salespersons: SalespersonOption[];
  market_segments: SegmentOption[];
}

export interface StatsFilters {
  salesperson_no?: string;
  market_segment?: string;
}

export interface UserResponseTime {
  user_id: string;
  user_name: string;
  avg_response_minutes: number;
  median_response_minutes: number;
  reply_count: number;
  fastest_minutes: number;
  slowest_minutes: number;
}

export interface InboxResponseTimeTrend {
  period: string;
  avg_response_minutes: number;
  reply_count: number;
}

export interface InboxResponseTimeStats {
  avg_response_minutes: number | null;
  median_response_minutes: number | null;
  total_replies_tracked: number;
  total_inbound_with_reply: number;
  by_user: UserResponseTime[];
  trend: InboxResponseTimeTrend[];
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

function buildFilterParams(filters?: StatsFilters): string {
  if (!filters) return '';
  let params = '';
  if (filters.salesperson_no) {
    params += `&salesperson_no=${encodeURIComponent(filters.salesperson_no)}`;
  }
  if (filters.market_segment) {
    params += `&market_segment=${encodeURIComponent(filters.market_segment)}`;
  }
  return params;
}


// ---------------------------------------------------------------------------
// ROI — the Save Ledger (problems the system caught and made good)
// ---------------------------------------------------------------------------

export type RoiSaveKind =
  | 'certificate_notice_backfilled'
  | 'order_verifier_correction'
  | 'pre_ship_correction'
  | 'short_ship_prevented'
  | 'ghost_order_recovered'
  | 'freight_recovered'
  | 'customer_email_misroute_prevented'
  | 'invoice_exception_presuggested';

export type RoiSaveStatus = 'candidate' | 'verified' | 'rejected';

export interface RoiSave {
  id: string;
  save_kind: RoiSaveKind | string;
  status: RoiSaveStatus | string;
  source_system: string;
  sage_order_no: string | null;
  customer_no: string | null;
  customer_name: string | null;
  headline: string;
  detail: string | null;
  amount_at_risk_usd: number | null;
  order_value_usd: number | null;
  detected_at: string | null;
  made_good_at: string | null;
  adjudicated_by: 'system' | 'human' | string;
  created_at: string;
}

export interface RoiSaveList {
  saves: RoiSave[];
  total: number;
}

export interface RoiKindSummary {
  save_kind: RoiSaveKind | string;
  count: number;
  amount_at_risk_usd: number;
  order_value_usd: number;
}

export interface RoiWeekPoint {
  week_start: string;
  count: number;
  amount_at_risk_usd: number;
}

export interface RoiSaveSummary {
  total_saves: number;
  verified_saves: number;
  candidate_saves: number;
  rejected_saves: number;
  amount_at_risk_usd: number;
  order_value_protected_usd: number;
  saves_last_30_days: number;
  by_kind: RoiKindSummary[];
  weekly: RoiWeekPoint[];
  latest_made_good_at: string | null;
}

export interface RoiSaveFilters {
  saveKind?: RoiSaveKind | string;
  status?: RoiSaveStatus | string;
  since?: Date;
  limit?: number;
}


export interface TrackRecordRun {
  id: string;
  automation_key: string;
  run_kind: string;
  status: string;
  window_start: string | null;
  window_end: string | null;
  started_at: string;
  finished_at: string | null;
  summary: string | null;
  examined_count: number;
  found_count: number;
  actioned_count: number;
  metrics: Record<string, unknown>;
  references: Record<string, unknown>;
  recorded_by: string;
  findings_count: number;
  created_at: string;
}

export interface TrackRecordRunList {
  runs: TrackRecordRun[];
  total: number;
}


export interface TrackRecordThreadEvent {
  id: string;
  seq: number;
  event_kind: string;
  actor: string;
  occurred_at: string;
  run_id: string | null;
  status_before: string | null;
  status_after: string | null;
  summary: string;
  details: Record<string, unknown>;
  evidence: Record<string, unknown>;
  recorded_by: string;
}

export interface TrackRecordThread {
  id: string;
  subject_type: string;
  subject_ref: string;
  thread_kind: string;
  title: string;
  status: string;
  severity: string;
  owner_automation_key: string | null;
  pending_action: string | null;
  opened_at: string;
  last_event_at: string;
  closed_at: string | null;
  links: Record<string, unknown>;
  details: Record<string, unknown>;
  event_count: number;
  events: TrackRecordThreadEvent[];
}

export interface TrackRecordThreadList {
  threads: TrackRecordThread[];
  total: number;
}

export const statisticsService = {
  getFilterOptions: async (): Promise<FilterOptions> => {
    const response = await api.get<FilterOptions>('/stats/filter-options');
    return response.data;
  },

  getOverview: async (dateFrom: Date, dateTo: Date, filters?: StatsFilters): Promise<OverviewStats> => {
    const response = await api.get<OverviewStats>(
      `/stats/overview?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}${buildFilterParams(filters)}`
    );
    return response.data;
  },

  getQuoteWinRate: async (dateFrom: Date, dateTo: Date, granularity = 'month', filters?: StatsFilters): Promise<TrendDataPoint[]> => {
    const response = await api.get<TrendDataPoint[]>(
      `/stats/quote-win-rate?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}&granularity=${granularity}${buildFilterParams(filters)}`
    );
    return response.data;
  },

  getResponseTimes: async (dateFrom: Date, dateTo: Date, granularity = 'month'): Promise<TrendDataPoint[]> => {
    const response = await api.get<TrendDataPoint[]>(
      `/stats/response-times?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}&granularity=${granularity}`
    );
    return response.data;
  },

  getSalespersonPerformance: async (dateFrom: Date, dateTo: Date, limit = 10, filters?: StatsFilters): Promise<SalespersonStats[]> => {
    const response = await api.get<SalespersonStats[]>(
      `/stats/salesperson-performance?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}&limit=${limit}${buildFilterParams(filters)}`
    );
    return response.data;
  },

  getQuotePipeline: async (dateFrom: Date, dateTo: Date, filters?: StatsFilters): Promise<QuotePipelineStats> => {
    const response = await api.get<QuotePipelineStats>(
      `/stats/quote-pipeline?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}${buildFilterParams(filters)}`
    );
    return response.data;
  },

  getRoutingSummary: async (dateFrom: Date, dateTo: Date): Promise<RoutingSummary> => {
    const response = await api.get<RoutingSummary>(
      `/stats/routing-summary?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}`
    );
    return response.data;
  },

  getInboxResponseTimes: async (
    dateFrom: Date,
    dateTo: Date,
    granularity = 'week',
    userId?: string,
  ): Promise<InboxResponseTimeStats> => {
    let url = `/stats/inbox-response-times?date_from=${formatDate(dateFrom)}&date_to=${formatDate(dateTo)}&granularity=${granularity}`;
    if (userId) url += `&user_id=${encodeURIComponent(userId)}`;
    const response = await api.get<InboxResponseTimeStats>(url);
    return response.data;
  },
  getRoiSaves: async (filters?: RoiSaveFilters): Promise<RoiSaveList> => {
    const params = new URLSearchParams();
    if (filters?.saveKind) params.set('save_kind', filters.saveKind);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.since) params.set('since', formatDate(filters.since));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    const response = await api.get<RoiSaveList>(`/stats/roi/saves${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  getRoiSaveSummary: async (): Promise<RoiSaveSummary> => {
    const response = await api.get<RoiSaveSummary>('/stats/roi/saves/summary');
    return response.data;
  },
  getTrackRecordRuns: async (automationKey?: string, limit = 50): Promise<TrackRecordRunList> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (automationKey) params.set('automation_key', automationKey);
    const response = await api.get<TrackRecordRunList>(`/stats/track-record/runs?${params.toString()}`);
    return response.data;
  },
  getTrackRecordThreads: async (openOnly = true, limit = 50): Promise<TrackRecordThreadList> => {
    const params = new URLSearchParams({ open_only: String(openOnly), limit: String(limit) });
    const response = await api.get<TrackRecordThreadList>(`/stats/track-record/threads?${params.toString()}`);
    return response.data;
  },
};
